import type { components } from "@restomenu/core/types";
import type {
  MenuCategory,
  MenuProduct,
  MenuProductCategoryBind,
  MenuProductImage,
  MenuProductTag,
} from "@/types";

type GuestCategory = components["schemas"]["GuestCategory"];
type GuestProduct = components["schemas"]["GuestProduct"];
type GuestProductVariant = components["schemas"]["GuestProductVariant"];
type GuestProductVariantGroup =
  components["schemas"]["GuestProductVariantGroup"];
type GuestListItem = GuestProduct | GuestProductVariantGroup;

export interface MapContext {
  tagsById: Record<string, MenuProductTag>;
  imageUrls: Record<string, string>;
}

function mapImages(
  brandId: string,
  imageBinds: readonly string[],
  imageUrls: Record<string, string>,
): MenuProductImage[] {
  const images: MenuProductImage[] = [];
  for (const id of imageBinds) {
    const url = imageUrls[id];
    // Непришедшую картинку пропускаем молча: пачки грузятся отдельно и
    // догоняют, а карточка без обложки читается, в отличие от битой.
    if (url) images.push({ id, brandId, url });
  }
  return images;
}

function mapTags(
  binds: readonly string[],
  tagsById: Record<string, MenuProductTag>,
): MenuProductTag[] {
  const tags: MenuProductTag[] = [];
  for (const id of binds) {
    const tag = tagsById[id];
    if (tag) tags.push(tag);
  }
  return tags;
}

function mapCategoryBinds(
  binds: readonly { categoryId: string; productPriority: string }[],
): MenuProductCategoryBind[] {
  return binds.map((bind) => ({
    categoryId: bind.categoryId,
    priority: bind.productPriority,
  }));
}

/** Товар или вариант — общая часть GuestProductBase. */
function mapLeaf(
  item: GuestProduct | GuestProductVariant,
  ctx: MapContext,
): MenuProduct {
  return {
    type: item.type,
    id: item.id,
    brandId: item.brandId,
    slug: item.slug,
    name: item.name,
    isEnabled: item.isEnabled,
    isAvailable: item.isAvailable,
    description: item.description,
    calories: item.calories,
    carbohydrates: item.carbohydrates,
    fats: item.fats,
    protein: item.protein,
    unit: item.unit,
    unitValue: item.unitValue,
    productTags: mapTags(item.productTagBinds, ctx.tagsById),
    categoryBinds: mapCategoryBinds(item.categoryBinds),
    optionGroupBinds: item.optionGroupBinds,
    images: mapImages(item.brandId, item.imageBinds, ctx.imageUrls),
    pricePoint: item.price,
    // Сужение по литеральному типу, а не `"variantSettings" in item`:
    // у GuestProductVariant поле объявлено обязательным, и дискриминант
    // даёт TypeScript гарантию, которой `in` не даёт.
    variantSettings:
      item.type === "productVariant"
        ? {
            productVariantPropertyBinds:
              item.variantSettings.productVariantPropertyBinds,
            isMain: item.variantSettings.isMain,
          }
        : null,
  };
}

/**
 * Карточка группы вариантов.
 *
 * GuestProductVariantGroup не несёт НИ ОДНОГО поля для отрисовки: состав
 * закрыт additionalProperties: false и ограничен
 * {type, id, brandId, isEnabled, productVariantPropertyBinds, productVariants}.
 * Ни имени, ни цены, ни картинок, ни categoryBinds — то есть группу нечем
 * подписать в списке и некуда положить внутри категории. Карточку собирает
 * главный вариант (спека §3.4).
 *
 * `id` остаётся идентификатором ВАРИАНТА, а не группы — паритет с прежним
 * normalize.ts, который делал {...mainVariant, __typename: "..."}. По этому
 * id строится позиция корзины и глубокая ссылка на модалку товара; менять
 * контракт замороженного до Ф7 домена заказов Ф4б не должна.
 *
 * null означает «рисовать нечем»: у группы не осталось вариантов.
 * С круга 6 состав группы сужен теми же условиями, что и выдача, поэтому
 * isAvailable внутри группы по контракту всегда true и до отбраковки по
 * нему дело не доходит — она осталась страховкой от регресса бэка.
 *
 * Оговорка про «по контракту» не случайна: живьём это не воспроизведено —
 * на деве нет ни одной группы вариантов (проверено 02.09 по трём точкам
 * стенда, все три отдают только type: product). Верхний уровень при этом
 * подтверждён: isAvailable там true везде.
 */
function mapGroup(
  group: GuestProductVariantGroup,
  ctx: MapContext,
): MenuProduct | null {
  const variants = group.productVariants;
  if (variants.length === 0) return null;

  const available = variants.filter((variant) => variant.isAvailable);
  if (available.length === 0) return null;

  // Карточку представляет ОДИН вариант целиком — имя, картинки, категории,
  // цена и id. Главный, если он есть в составе и доступен; иначе первый
  // доступный. С круга 6 сужение состава может не оставить в группе
  // варианта с isMain вовсе: верхний элемент выдачи сервер выбирает
  // порядком сортировки, а не этим признаком.
  //
  // Раздать поля разным вариантам нельзя, хотя соблазн есть («имя пусть
  // всегда у главного, чтобы карточка не прыгала между точками»): id
  // карточки уезжает в позицию корзины, когда гость жмёт «в корзину», не
  // раскрыв варианты. Имя от одного варианта с ценой от другого дали бы
  // заказ несуществующего здесь товара по цене соседнего.
  const main = variants.find((variant) => variant.variantSettings.isMain);
  const face = mapLeaf(main?.isAvailable ? main : available[0], ctx);

  return {
    ...face,
    type: "productVariantGroup",
    isEnabled: group.isEnabled,
    // Сама группа в списке доступна — недоступную мы уже отбраковали выше.
    isAvailable: true,
    variantSettings: null,
    variantGroup: {
      productVariantPropertyBinds: group.productVariantPropertyBinds,
    },
    // Варианты отдаём целиком, как приехали. Отбраковку сделал сервер:
    // с круга 6 недоступного в составе не бывает, и «недоступный размер»
    // в карточке — ветка, до которой дойти нечем.
    variants: variants.map((variant) => mapLeaf(variant, ctx)),
  };
}

export function mapGuestProduct(
  item: GuestListItem,
  ctx: MapContext,
): MenuProduct | null {
  if (item.type === "productVariantGroup") return mapGroup(item, ctx);
  return mapLeaf(item, ctx);
}

export function mapGuestCategory(
  category: GuestCategory,
  imageUrls: Record<string, string>,
): MenuCategory {
  return {
    id: category.id,
    brandId: category.brandId,
    name: category.name,
    slug: category.slug,
    priority: category.priority,
    isEnabled: category.isEnabled,
    imageBind: category.imageBind,
    imageUrl: category.imageBind
      ? (imageUrls[category.imageBind] ?? null)
      : null,
  };
}
